/**
 * Step 8: Your Story
 *
 * Collects business stats, credentials/certifications, and testimonials
 * to build trust and credibility on the website.
 */

import { useState } from 'react';
import { ArrowLeft, ArrowRight, Plus, Trash2, Award, Star, TrendingUp, Quote } from 'lucide-react';
import type { Step8Data, BusinessStat, Credential, Testimonial } from '../../../types/onboarding';

interface Step8Props {
  data: Step8Data | null;
  onChange: (data: Step8Data) => void;
  onNext: () => void;
  onBack: () => void;
  saving: boolean;
  // Pre-populated from Step 4 if available
  yearsInBusinessFromStep4?: number;
}

// Default stat label types
const STAT_LABEL_OPTIONS = [
  { value: 'years_in_business', label: 'Years in Business' },
  { value: 'years_experience', label: 'Years of Experience' },
  { value: 'projects_completed', label: 'Projects Completed' },
  { value: 'happy_customers', label: 'Happy Customers' },
  { value: 'clients_served', label: 'Clients Served' },
  { value: 'custom', label: 'Custom Label' },
] as const;

// Default empty stat
const createEmptyStat = (): BusinessStat => ({
  label_type: 'years_in_business',
  value: 0,
  skip: false,
});

// Default empty credential
const createEmptyCredential = (): Credential => ({
  name: '',
  issuer: '',
  license_number: '',
});

// Default empty testimonial
const createEmptyTestimonial = (): Testimonial => ({
  quote: '',
  author_name: '',
  rating: 5,
});

export function Step8YourStory({
  data,
  onChange,
  onNext,
  onBack,
  saving,
  yearsInBusinessFromStep4,
}: Step8Props) {
  // Initialize state from data or defaults
  // If no stats exist and years_in_business was provided in Step 4, pre-populate it
  const [stats, setStats] = useState<BusinessStat[]>(() => {
    if (data?.stats && data.stats.length > 0) {
      return data.stats;
    }
    // Pre-populate with years in business from Step 4 if available
    if (yearsInBusinessFromStep4 && yearsInBusinessFromStep4 > 0) {
      return [{
        label_type: 'years_in_business',
        value: yearsInBusinessFromStep4,
        skip: false,
      }];
    }
    return [createEmptyStat()];
  });
  const [credentials, setCredentials] = useState<Credential[]>(
    data?.credentials || []
  );
  const [testimonials, setTestimonials] = useState<Testimonial[]>(
    data?.testimonials || []
  );
  const [usePlaceholderTestimonials, setUsePlaceholderTestimonials] = useState(
    data?.use_placeholder_testimonials || false
  );

  // Save current state to parent
  const saveData = () => {
    onChange({
      // Filter stats: must not be skipped and have a value > 0
      // For custom stats, also check that custom_label is provided
      stats: stats.filter(s => {
        if (s.skip) return false;
        if (s.value <= 0) return false;
        if (s.label_type === 'custom' && !s.custom_label?.trim()) return false;
        return true;
      }),
      credentials: credentials.filter(c => c.name.trim()),
      testimonials: testimonials.filter(t => t.quote.trim() && t.author_name.trim()),
      use_placeholder_testimonials: usePlaceholderTestimonials,
    });
  };

  // Stats handlers
  const updateStat = (index: number, updates: Partial<BusinessStat>) => {
    const newStats = [...stats];
    newStats[index] = { ...newStats[index], ...updates };
    setStats(newStats);
  };

  const addStat = () => {
    setStats([...stats, createEmptyStat()]);
  };

  const removeStat = (index: number) => {
    setStats(stats.filter((_, i) => i !== index));
  };

  // Credentials handlers
  const updateCredential = (index: number, updates: Partial<Credential>) => {
    const newCredentials = [...credentials];
    newCredentials[index] = { ...newCredentials[index], ...updates };
    setCredentials(newCredentials);
  };

  const addCredential = () => {
    setCredentials([...credentials, createEmptyCredential()]);
  };

  const removeCredential = (index: number) => {
    setCredentials(credentials.filter((_, i) => i !== index));
  };

  // Testimonials handlers
  const updateTestimonial = (index: number, updates: Partial<Testimonial>) => {
    const newTestimonials = [...testimonials];
    newTestimonials[index] = { ...newTestimonials[index], ...updates };
    setTestimonials(newTestimonials);
  };

  const addTestimonial = () => {
    setTestimonials([...testimonials, createEmptyTestimonial()]);
  };

  const removeTestimonial = (index: number) => {
    setTestimonials(testimonials.filter((_, i) => i !== index));
  };

  // Can always proceed (all fields are optional)
  const canProceed = true;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Your Story
        </h1>
        <p className="text-gray-600">
          Share your achievements, credentials, and happy customer stories to build trust
        </p>
      </div>

      {/* Business Stats Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-primary-600" />
          <h2 className="text-lg font-semibold text-gray-900">Business Stats</h2>
        </div>
        <p className="text-sm text-gray-500">
          Impressive numbers help build credibility. Skip any that don't apply.
        </p>

        <div className="space-y-4">
          {stats.map((stat, index) => (
            <div
              key={index}
              className={`bg-white border rounded-xl p-4 ${
                stat.skip ? 'border-gray-200 opacity-60' : 'border-gray-300'
              }`}
            >
              <div className="flex items-start gap-4">
                <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Label type selector */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Stat Type
                    </label>
                    <select
                      value={stat.label_type}
                      onChange={(e) => updateStat(index, { label_type: e.target.value as BusinessStat['label_type'] })}
                      onBlur={saveData}
                      disabled={stat.skip}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100"
                    >
                      {STAT_LABEL_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Custom label (only shown if type is 'custom') */}
                  {stat.label_type === 'custom' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Custom Label
                      </label>
                      <input
                        type="text"
                        value={stat.custom_label || ''}
                        onChange={(e) => updateStat(index, { custom_label: e.target.value })}
                        onBlur={saveData}
                        disabled={stat.skip}
                        placeholder="e.g., Bowls Served"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100"
                      />
                      <p className="text-xs text-gray-400 mt-1">The label that appears under your number</p>
                    </div>
                  )}

                  {/* Value */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Value
                    </label>
                    <input
                      type="number"
                      value={stat.value || ''}
                      onChange={(e) => updateStat(index, { value: parseInt(e.target.value) || 0 })}
                      onBlur={saveData}
                      disabled={stat.skip}
                      min="0"
                      placeholder={stat.label_type === 'custom' ? 'e.g., 23700' : 'e.g., 15'}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100"
                    />
                    <p className="text-xs text-gray-400 mt-1">Numbers only (formatting added automatically)</p>
                  </div>
                </div>

                {/* Skip toggle and remove */}
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={stat.skip || false}
                      onChange={(e) => updateStat(index, { skip: e.target.checked })}
                      onBlur={saveData}
                      className="w-4 h-4 text-gray-500 rounded"
                    />
                    Skip
                  </label>
                  {stats.length > 1 && (
                    <button
                      onClick={() => removeStat(index)}
                      className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}

          {stats.length < 4 && (
            <button
              onClick={addStat}
              className="flex items-center gap-2 px-4 py-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add another stat
            </button>
          )}
        </div>
      </div>

      {/* Credentials Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Award className="w-5 h-5 text-primary-600" />
          <h2 className="text-lg font-semibold text-gray-900">Credentials & Certifications</h2>
          <span className="text-sm text-gray-400">(Optional)</span>
        </div>
        <p className="text-sm text-gray-500">
          Licenses, certifications, and awards that show your expertise
        </p>

        <div className="space-y-4">
          {credentials.map((credential, index) => (
            <div
              key={index}
              className="bg-white border border-gray-300 rounded-xl p-4"
            >
              <div className="flex items-start gap-4">
                <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Credential Name *
                    </label>
                    <input
                      type="text"
                      value={credential.name}
                      onChange={(e) => updateCredential(index, { name: e.target.value })}
                      onBlur={saveData}
                      placeholder="e.g., Licensed Contractor"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Issuing Organization
                    </label>
                    <input
                      type="text"
                      value={credential.issuer || ''}
                      onChange={(e) => updateCredential(index, { issuer: e.target.value })}
                      onBlur={saveData}
                      placeholder="e.g., State Board"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      License Number
                    </label>
                    <input
                      type="text"
                      value={credential.license_number || ''}
                      onChange={(e) => updateCredential(index, { license_number: e.target.value })}
                      onBlur={saveData}
                      placeholder="e.g., #123456"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>
                <button
                  onClick={() => removeCredential(index)}
                  className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}

          {credentials.length < 5 && (
            <button
              onClick={addCredential}
              className="flex items-center gap-2 px-4 py-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add credential
            </button>
          )}
        </div>
      </div>

      {/* Testimonials Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Quote className="w-5 h-5 text-primary-600" />
          <h2 className="text-lg font-semibold text-gray-900">Customer Testimonials</h2>
          <span className="text-sm text-gray-400">(Optional)</span>
        </div>
        <p className="text-sm text-gray-500">
          Real reviews from happy customers make a powerful impression
        </p>

        {/* Placeholder toggle */}
        <div className="bg-gray-50 rounded-xl p-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={usePlaceholderTestimonials}
              onChange={(e) => {
                setUsePlaceholderTestimonials(e.target.checked);
                saveData();
              }}
              className="w-5 h-5 mt-0.5 text-primary-600 rounded"
            />
            <div>
              <span className="font-medium text-gray-900">
                Use placeholder testimonials for now
              </span>
              <p className="text-sm text-gray-500 mt-1">
                We'll create realistic placeholder testimonials while you gather real ones.
                You can replace them later from your dashboard.
              </p>
            </div>
          </label>
        </div>

        {!usePlaceholderTestimonials && (
          <div className="space-y-4">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-white border border-gray-300 rounded-xl p-4"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-1 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Customer Quote *
                      </label>
                      <textarea
                        value={testimonial.quote}
                        onChange={(e) => updateTestimonial(index, { quote: e.target.value })}
                        onBlur={saveData}
                        rows={3}
                        placeholder="e.g., They transformed our backyard into an oasis. Professional, on-time, and the results exceeded our expectations!"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Customer Name *
                        </label>
                        <input
                          type="text"
                          value={testimonial.author_name}
                          onChange={(e) => updateTestimonial(index, { author_name: e.target.value })}
                          onBlur={saveData}
                          placeholder="e.g., Sarah M."
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Rating
                        </label>
                        <div className="flex items-center gap-1">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <button
                              key={star}
                              type="button"
                              onClick={() => {
                                updateTestimonial(index, { rating: star });
                                saveData();
                              }}
                              className="p-1 hover:scale-110 transition-transform"
                            >
                              <Star
                                className={`w-6 h-6 ${
                                  star <= testimonial.rating
                                    ? 'fill-yellow-400 text-yellow-400'
                                    : 'text-gray-300'
                                }`}
                              />
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => removeTestimonial(index)}
                    className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}

            {testimonials.length < 5 && (
              <button
                onClick={addTestimonial}
                className="flex items-center gap-2 px-4 py-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add testimonial
              </button>
            )}
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-4">
        <button
          onClick={onBack}
          className="flex items-center gap-2 px-6 py-3 rounded-lg font-medium text-gray-600 hover:bg-gray-100 transition-all"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <button
          onClick={() => {
            saveData();
            onNext();
          }}
          disabled={!canProceed || saving}
          className={`
            flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all
            ${
              canProceed
                ? 'bg-primary-600 text-white hover:bg-primary-700'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }
          `}
        >
          Continue
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
